import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import API from '../api/axios';
import { toast } from 'react-toastify';
import { FiPlus, FiX, FiTrash2, FiSearch, FiEdit2, FiEye } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import './Workshop.css';

const SHAPE_LABEL = { rectangle: 'Rectangle', circle: 'Circle', custom: 'Custom' };
const STATUS_CFG = {
  pending:     { label: 'Pending',     bg: '#f8fafc', color: '#64748b', border: '#e2e8f0' },
  in_progress: { label: 'In Progress', bg: '#fffbeb', color: '#d97706', border: '#fde68a' },
  completed:   { label: 'Completed',   bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
};
const NEXT_STATUS = { pending: 'in_progress', in_progress: 'completed', completed: 'pending' };

function fmt(iso) { if (!iso) return '—'; return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }

// Area in SQ/FT — rectangle: H×W, circle: π×r², custom: not computed (free-text dimension)
function calcArea(j) {
  if (j.shape === 'circle') {
    const r = (+j.diameterFt || 0) / 2;
    return r > 0 ? Math.PI * r * r : 0;
  }
  if (j.shape === 'custom') return 0;
  return (+j.heightFt || 0) * (+j.widthFt || 0);
}

export default function Workshop() {
  const { canAccess } = useAuth();
  const canCreate = canAccess('workshop-jobs', 'create');
  const canEdit   = canAccess('workshop-jobs', 'edit');
  const canDelete = canAccess('workshop-jobs', 'delete');

  const [jobs, setJobs]           = useState([]);
  const [projects, setProjects]   = useState([]);
  const [search, setSearch]       = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading]     = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editJob, setEditJob]     = useState(null);
  const [viewJob, setViewJob]     = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const r = await API.get('/workshop-jobs');
      setJobs(r.data);
    } catch { toast.error('Failed to load workshop jobs'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    API.get('/projects').then(r => setProjects(r.data)).catch(() => {});
  }, []);

  const filtered = jobs.filter(j => {
    if (statusFilter && j.status !== statusFilter) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (j.jobNumber || '').toLowerCase().includes(s)
      || (j.itemName || '').toLowerCase().includes(s)
      || (j.project?.title || '').toLowerCase().includes(s);
  });

  async function cycleStatus(job) {
    try {
      const r = await API.put(`/workshop-jobs/${job._id}`, { ...job, project: job.project?._id || job.project, status: NEXT_STATUS[job.status] });
      setJobs(prev => prev.map(j => j._id === r.data._id ? r.data : j));
      toast.success(`Status → ${STATUS_CFG[r.data.status].label}`);
    } catch { toast.error('Failed to update status'); }
  }

  async function deleteJob(id) {
    try {
      await API.delete(`/workshop-jobs/${id}`);
      setJobs(prev => prev.filter(j => j._id !== id));
      setConfirmDel(null);
      toast.success('Job deleted');
    } catch { toast.error('Delete failed'); }
  }

  return (
    <Layout>
      <div className="ws-root">
        <div className="ws-toolbar">
          <div className="ws-search-box">
            <FiSearch size={13} className="ws-search-icon" />
            <input placeholder="Search jobs..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="ws-filter-tabs">
            <button className={`ws-ftab ${!statusFilter ? 'ws-ftab-active' : ''}`} onClick={() => setStatusFilter('')}>All</button>
            {Object.entries(STATUS_CFG).map(([k, v]) => (
              <button key={k} className={`ws-ftab ${statusFilter === k ? 'ws-ftab-active' : ''}`} onClick={() => setStatusFilter(k)}>{v.label}</button>
            ))}
          </div>
          {canCreate && (
            <button className="ws-add-btn" onClick={() => { setEditJob(null); setShowModal(true); }}>
              <FiPlus size={13}/> New Workshop Job
            </button>
          )}
        </div>

        <div className="ws-card">
          {loading ? (
            <div className="ws-empty">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="ws-empty">No workshop jobs found. Click "+ New Workshop Job" to create one.</div>
          ) : (
            <table className="ws-table">
              <thead>
                <tr>
                  <th>Job #</th><th>Item Name</th><th>Project</th><th>Shape</th>
                  <th>Dimensions</th><th>Total Area (SQ/FT)</th><th>Qty</th>
                  <th>Sent Date</th><th>Status</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(j => {
                  const area = calcArea(j);
                  return (
                  <tr key={j._id}>
                    <td className="ws-td-num">{j.jobNumber}</td>
                    <td className="ws-td-name">{j.itemName}</td>
                    <td>{j.project?.title || <span className="ws-muted">Standalone</span>}</td>
                    <td>{SHAPE_LABEL[j.shape]}</td>
                    <td>
                      {j.shape === 'circle' ? `⌀ ${j.diameterFt ?? '–'} ft`
                        : j.shape === 'custom' ? (j.dimensionNote || <span className="ws-muted">—</span>)
                        : `${j.heightFt ?? '–'} × ${j.widthFt ?? '–'} ft`}
                    </td>
                    <td>{area > 0 ? area.toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '–'}</td>
                    <td>{j.qty}</td>
                    <td>{fmt(j.createdAt)}</td>
                    <td>
                      {canEdit ? (
                        <button
                          className="ws-status-btn"
                          style={{ background: STATUS_CFG[j.status].bg, color: STATUS_CFG[j.status].color, border: `1px solid ${STATUS_CFG[j.status].border}` }}
                          onClick={() => cycleStatus(j)}
                          title="Click to advance status"
                        >
                          {STATUS_CFG[j.status].label}
                        </button>
                      ) : (
                        <span
                          className="ws-status-btn"
                          style={{ background: STATUS_CFG[j.status].bg, color: STATUS_CFG[j.status].color, border: `1px solid ${STATUS_CFG[j.status].border}`, cursor: 'default' }}
                        >
                          {STATUS_CFG[j.status].label}
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="ws-row-acts">
                        <button className="ws-icon-btn" onClick={() => setViewJob(j)} title="View"><FiEye size={13}/></button>
                        {canEdit && <button className="ws-icon-btn" onClick={() => { setEditJob(j); setShowModal(true); }} title="Edit"><FiEdit2 size={13}/></button>}
                        {canDelete && <button className="ws-icon-btn ws-del" onClick={() => setConfirmDel(j)} title="Delete"><FiTrash2 size={13}/></button>}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <JobModal
          job={editJob}
          projects={projects}
          onClose={() => { setShowModal(false); setEditJob(null); }}
          onSaved={j => {
            setJobs(prev => editJob ? prev.map(x => x._id === j._id ? j : x) : [j, ...prev]);
            setShowModal(false); setEditJob(null);
            toast.success(editJob ? 'Job updated' : 'Job sent to workshop');
          }}
        />
      )}

      {viewJob && (
        <JobModal
          job={viewJob}
          projects={projects}
          readOnly
          onClose={() => setViewJob(null)}
          onSaved={() => setViewJob(null)}
        />
      )}

      {confirmDel && (
        <div className="ws-overlay" onClick={() => setConfirmDel(null)}>
          <div className="ws-confirm-box" onClick={e => e.stopPropagation()}>
            <h4>Delete Workshop Job</h4>
            <p>Delete "{confirmDel.jobNumber}"? This cannot be undone.</p>
            <div className="ws-confirm-actions">
              <button className="ws-confirm-cancel" onClick={() => setConfirmDel(null)}>Cancel</button>
              <button className="ws-confirm-ok" onClick={() => deleteJob(confirmDel._id)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function JobModal({ job, projects, onClose, onSaved, readOnly }) {
  const [form, setForm] = useState({
    project:  job?.project?._id || job?.project || '',
    itemName: job?.itemName || '',
    shape:    job?.shape || 'rectangle',
    heightFt: job?.heightFt ?? '',
    widthFt:  job?.widthFt  ?? '',
    diameterFt: job?.diameterFt ?? '',
    dimensionNote: job?.dimensionNote || '',
    qty:      job?.qty ?? 1,
    notes:    job?.notes || '',
    status:   job?.status || 'pending',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const areaPreview = form.shape === 'circle'
    ? (() => { const r = (+(form.diameterFt) || 0) / 2; return r > 0 ? Math.PI * r * r : 0; })()
    : form.shape === 'custom' ? 0
    : (+(form.heightFt) || 0) * (+(form.widthFt) || 0);

  async function save() {
    if (!form.itemName.trim()) return toast.error('Item name is required');
    setSaving(true);
    try {
      const payload = { ...form, project: form.project || undefined };
      const r = job
        ? await API.put(`/workshop-jobs/${job._id}`, payload)
        : await API.post('/workshop-jobs', payload);
      onSaved(r.data);
    } catch (e) { toast.error(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  }

  return (
    <div className="ws-overlay">
      <div className="ws-modal" onClick={e => e.stopPropagation()}>
        <div className="ws-modal-header">
          <h3>{readOnly ? 'View Workshop Job' : job ? 'Edit Workshop Job' : 'New Workshop Job'}</h3>
          <button className="ws-modal-close" onClick={onClose}><FiX /></button>
        </div>
        <fieldset className="ws-modal-body" disabled={readOnly}>
          <div className="ws-mrow">
            <div className="ws-mgroup ws-mfull">
              <label>Item Name *</label>
              <input value={form.itemName} onChange={e => set('itemName', e.target.value)} placeholder="e.g. Shower Partition Glass" autoFocus />
            </div>
          </div>
          <div className="ws-mrow">
            <div className="ws-mgroup">
              <label>Project <span className="ws-opt">(optional)</span></label>
              <select value={form.project} onChange={e => set('project', e.target.value)}>
                <option value="">Standalone (no project)</option>
                {projects.map(p => <option key={p._id} value={p._id}>{p.title}</option>)}
              </select>
            </div>
            <div className="ws-mgroup">
              <label>Shape</label>
              <select value={form.shape} onChange={e => set('shape', e.target.value)}>
                <option value="rectangle">Rectangle</option>
                <option value="circle">Circle</option>
                <option value="custom">Custom</option>
              </select>
            </div>
          </div>
          {form.shape === 'rectangle' && (
            <div className="ws-mrow">
              <div className="ws-mgroup">
                <label>Height (ft)</label>
                <input type="number" min="0" step="0.01" value={form.heightFt} onChange={e => set('heightFt', e.target.value)} placeholder="0" />
              </div>
              <div className="ws-mgroup">
                <label>Width (ft)</label>
                <input type="number" min="0" step="0.01" value={form.widthFt} onChange={e => set('widthFt', e.target.value)} placeholder="0" />
              </div>
              <div className="ws-mgroup">
                <label>Qty</label>
                <input type="number" min="1" value={form.qty} onChange={e => set('qty', +e.target.value)} />
              </div>
            </div>
          )}
          {form.shape === 'circle' && (
            <div className="ws-mrow">
              <div className="ws-mgroup">
                <label>Diameter (ft)</label>
                <input type="number" min="0" step="0.01" value={form.diameterFt} onChange={e => set('diameterFt', e.target.value)} placeholder="0" />
              </div>
              <div className="ws-mgroup">
                <label>Qty</label>
                <input type="number" min="1" value={form.qty} onChange={e => set('qty', +e.target.value)} />
              </div>
            </div>
          )}
          {form.shape === 'custom' && (
            <div className="ws-mrow">
              <div className="ws-mgroup" style={{ flex: 3 }}>
                <label>Dimension Details</label>
                <input value={form.dimensionNote} onChange={e => set('dimensionNote', e.target.value)} placeholder="e.g. L-shaped, 4ft x 3ft with 1ft cutout" />
              </div>
              <div className="ws-mgroup">
                <label>Qty</label>
                <input type="number" min="1" value={form.qty} onChange={e => set('qty', +e.target.value)} />
              </div>
            </div>
          )}
          {areaPreview > 0 && (
            <div className="ws-amount-preview">Total Area: <strong>{areaPreview.toLocaleString('en-IN', { maximumFractionDigits: 2 })} SQ/FT</strong></div>
          )}
          {job && (
            <div className="ws-mrow">
              <div className="ws-mgroup ws-mfull">
                <label>Status</label>
                <select value={form.status} onChange={e => set('status', e.target.value)}>
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </div>
          )}
          <div className="ws-mgroup">
            <label>Notes for Workshop Team <span className="ws-opt">(optional)</span></label>
            <textarea rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any fabrication instructions..." />
          </div>
        </fieldset>
        <div className="ws-modal-footer">
          <button className="ws-btn-ghost" onClick={onClose}>{readOnly ? 'Close' : 'Cancel'}</button>
          {!readOnly && (
            <button className="ws-btn-primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : job ? 'Update Job' : 'Send to Workshop'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
